import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import CollapsibleFilters from './CollapsibleFilters';
import { 
  Activity, 
  Search, 
  Calendar, 
  User, 
  Filter, 
  Download, 
  FileText, 
  FileSpreadsheet,
  ChevronDown, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  Globe,
  Smartphone,
  Monitor,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';

interface UserActivity {
  id: number;
  user_id: number;
  action: string;
  description: string;
  category?: string;
  priority?: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  user_name: string;
  user_email: string;
  user_role: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

const UserActivityPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<number>(0);
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const todayStr = new Date().toISOString().split('T')[0];
  const [dateRange, setDateRange] = useState({ start: todayStr, end: todayStr });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const itemsPerPage = 25;

  // Toggle category expand/collapse
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Category groups based on sidebar pages
  const categoryGroups = [
    { value: 'all', label: t('activity.cat.all'), icon: '📋' },
    { value: 'auth', label: t('activity.cat.auth'), icon: '🔐' },
    { value: 'capex', label: t('activity.cat.capex'), icon: '💰' },
    { value: 'printer', label: t('activity.cat.printer'), icon: '🖨️' },
    { value: 'consumable', label: t('activity.cat.consumable'), icon: '📦' },
    { value: 'network', label: t('activity.cat.network'), icon: '🔌' },
    { value: 'industrial', label: t('activity.cat.industrial'), icon: '⚙️' },
    { value: 'order', label: t('activity.cat.order'), icon: '📋' },
    { value: 'technical', label: t('activity.cat.technical'), icon: '🔧' },
    { value: 'nvr_camera', label: t('activity.cat.nvr_camera'), icon: '📹' },
    { value: 'gsm_telefon', label: t('activity.cat.gsm_telefon'), icon: '📱' },
    { value: 'arac_gerec', label: t('activity.cat.arac_gerec'), icon: '🛠️' },
    { value: 'server', label: t('activity.cat.server'), icon: '🖥️' },
    { value: 'storage', label: t('activity.cat.storage'), icon: '💾' },
    { value: 'meeting', label: t('activity.cat.meeting'), icon: '📅' },
    { value: 'zimmet', label: t('activity.cat.zimmet'), icon: '📄' },
    { value: 'settings', label: t('activity.cat.settings'), icon: '⚙️' },
    { value: 'other', label: t('activity.cat.other'), icon: '📌' }
  ];

  // Action to category mapping based on sidebar pages
  const actionToCategoryMap: { [key: string]: string } = {
    // Auth operations
    'login': 'auth',
    'logout': 'auth',
    'failed_login': 'auth',
    'password_change': 'auth',
    'password_reset': 'auth',

    // Zimmet operations
    'create_assignment_form': 'zimmet',
    'create_return_form': 'zimmet',
    'device_assigned_to_me': 'zimmet',
    'device_returned_by_me': 'zimmet',
    'equipment_assigned': 'zimmet',
    'equipment_returned': 'zimmet',
    'zimmet_approved_digital': 'zimmet',
    'zimmet_rejected_digital': 'zimmet',
    'computer_assigned': 'zimmet',
    'monitor_assigned': 'zimmet',
    'mobile_phone_assigned': 'zimmet',
    
    // CAPEX operations
    'create_capex': 'capex',
    'update_capex': 'capex',
    'delete_capex': 'capex',
    'use_capex': 'capex',
    'block_capex': 'capex',
    'clear_blocked_capex': 'capex',
    'debt_created': 'capex',
    'clear_capex_block': 'capex',
    'unblock_capex': 'capex',
    
    // Printer operations
    'add_printer': 'printer',
    'update_printer': 'printer',
    'delete_printer': 'printer',
    'add_brand': 'printer',
    'update_brand': 'printer',
    'delete_brand': 'printer',
    'add_model': 'printer',
    'update_model': 'printer',
    'delete_model': 'printer',
    'upload_driver': 'printer',
    'download_driver': 'printer',
    'delete_switch_model': 'printer',
    'update_switch_model': 'printer',
    
    // Consumable operations
    'add_consumable': 'consumable',
    'update_consumable': 'consumable',
    'delete_consumable': 'consumable',
    'assign_consumable': 'consumable',
    
    // Network operations
    'create_network_switch': 'network',
    'update_network_switch': 'network',
    'delete_network_switch': 'network',
    'switch_backup': 'network',
    'create_vlan': 'network',
    'update_vlan': 'network',
    'delete_vlan': 'network',
    'create_circuit': 'network',
    'update_circuit': 'network',
    'delete_circuit': 'network',
    'cabinet_create': 'network',
    'cabinet_update': 'network',
    'cabinet_delete': 'network',
    
    // Industrial operations
    'create_iot_device': 'industrial',
    'update_iot_device': 'industrial',
    'delete_iot_device': 'industrial',
    'create_smart_scale': 'industrial',
    'update_smart_scale': 'industrial',
    'delete_smart_scale': 'industrial',
    'calibrate_smart_scale': 'industrial',
    'create_radio': 'industrial',
    'update_radio': 'industrial',
    'delete_radio': 'industrial',
    'assign_radio_user': 'industrial',
    'unassign_radio_user': 'industrial',
    'create_handheld_terminal': 'industrial',
    'update_handheld_terminal': 'industrial',
    'delete_handheld_terminal': 'industrial',
    
    // Order operations
    'create_order': 'order',
    'update_order': 'order',
    'delete_order': 'order',
    'approve_order': 'order',
    'complete_order': 'order',
    'cancel_order': 'order',
    'approve_invoice': 'order',
    'upload_invoice': 'order',
    'delete_invoice': 'order',
    
    // Technical service operations
    'create_technical_service': 'technical',
    'update_technical_service': 'technical',
    'delete_technical_service': 'technical',
    
    // GSM & Telefon operations
    'create_gsm_line': 'gsm_telefon',
    'update_gsm_line': 'gsm_telefon',
    'delete_gsm_line': 'gsm_telefon',
    'gsm_subscription_create': 'gsm_telefon',
    'gsm_subscription_status': 'gsm_telefon',
    'gsm_subscription_activate': 'gsm_telefon',
    'gsm_subscription_cancel': 'gsm_telefon',
    'gsm_subscription_delete': 'gsm_telefon',
    'gsm_document_upload': 'gsm_telefon',
    'gsm_document_delete': 'gsm_telefon',
    'quick_assign_gsm_line': 'gsm_telefon',
    'sql_import_gsm_line': 'gsm_telefon',
    'mobile_phone_create': 'gsm_telefon',
    'mobile_phone_update': 'gsm_telefon',
    'mobile_phone_delete': 'gsm_telefon',
    'mobile_phone_quick_assign': 'gsm_telefon',
    'mobile_phone_copy_paste_import': 'gsm_telefon',
    'create_tariff': 'gsm_telefon',
    'update_tariff': 'gsm_telefon',
    'delete_tariff': 'gsm_telefon',
    
    // Araç Gereç operations (Equipment, Companies, Locations, Suppliers, etc.)
    'add_equipment': 'arac_gerec',
    'update_equipment': 'arac_gerec',
    'delete_equipment': 'arac_gerec',
    'create_company': 'arac_gerec',
    'update_company': 'arac_gerec',
    'delete_company': 'arac_gerec',
    'create_location': 'arac_gerec',
    'update_location': 'arac_gerec',
    'delete_location': 'arac_gerec',
    'create_supplier': 'arac_gerec',
    'update_supplier': 'arac_gerec',
    'delete_supplier': 'arac_gerec',
    
    // Server operations
    'create_physical_server': 'server',
    'update_physical_server': 'server',
    'delete_physical_server': 'server',
    'create_virtual_server': 'server',
    'update_virtual_server': 'server',
    'delete_virtual_server': 'server',
    
    // Meeting operations
    'create_meeting': 'meeting',
    'update_meeting': 'meeting',
    'delete_meeting': 'meeting',
    
    // NVR & Camera operations
    'create_nvr': 'nvr_camera',
    'update_nvr': 'nvr_camera',
    'delete_nvr': 'nvr_camera',
    'create_camera': 'nvr_camera',
    'update_camera': 'nvr_camera',
    'delete_camera': 'nvr_camera',
    'add_camera': 'nvr_camera',
    'remove_camera': 'nvr_camera',
    
    // Storage operations
    'create_storage_unit': 'storage',
    'update_storage_unit': 'storage',
    'delete_storage_unit': 'storage',
    
    // Settings operations
    'create_user': 'settings',
    'update_user': 'settings',
    'delete_user': 'settings',
    'create_announcement': 'settings',
    'update_announcement': 'settings',
    'delete_announcement': 'settings',
    'upload_logo': 'settings',
    'delete_logo': 'settings',
    'upload_favicon': 'settings',
    
    // Other operations
    'view': 'other',
    'export': 'other',
    'import': 'other'
  };

  // Action type labels (language-aware)
  const actionTypeLabels: { [key: string]: string } = language === 'en' ? {
    'login': 'Login', 'logout': 'Logout', 'failed_login': 'Failed Login',
    'create_user': 'Create User', 'update_user': 'Update User', 'delete_user': 'Delete User',
    'create_order': 'Create Order', 'update_order': 'Update Order', 'delete_order': 'Delete Order',
    'approve_order': 'Approve Order', 'complete_order': 'Complete Order', 'cancel_order': 'Cancel Order',
    'create_capex': 'Create CAPEX', 'update_capex': 'Update CAPEX', 'delete_capex': 'Delete CAPEX',
    'use_capex': 'Use CAPEX', 'block_capex': 'Block CAPEX', 'clear_blocked_capex': 'Unblock CAPEX',
    'add_equipment': 'Add Equipment', 'update_equipment': 'Update Equipment', 'delete_equipment': 'Delete Equipment',
    'add_consumable': 'Add Consumable', 'update_consumable': 'Update Consumable', 'delete_consumable': 'Delete Consumable', 'assign_consumable': 'Assign Consumable',
    'add_printer': 'Add Printer', 'update_printer': 'Update Printer', 'delete_printer': 'Delete Printer',
    'add_brand': 'Add Brand', 'update_brand': 'Update Brand', 'delete_brand': 'Delete Brand',
    'add_model': 'Add Model', 'update_model': 'Update Model', 'delete_model': 'Delete Model',
    'create_location': 'Create Location', 'update_location': 'Update Location', 'delete_location': 'Delete Location',
    'create_company': 'Create Company', 'update_company': 'Update Company', 'delete_company': 'Delete Company',
    'create_supplier': 'Create Supplier', 'update_supplier': 'Update Supplier', 'delete_supplier': 'Delete Supplier',
    'create_gsm_line': 'Create GSM Line', 'update_gsm_line': 'Update GSM Line', 'delete_gsm_line': 'Delete GSM Line', 'quick_assign_gsm_line': 'Quick Assign GSM Line', 'gsm_subscription_create': 'Create GSM Subscription Request', 'gsm_subscription_status': 'GSM Subscription Step Update', 'gsm_subscription_activate': 'Activate GSM Line', 'gsm_subscription_cancel': 'Cancel GSM Subscription Request', 'gsm_subscription_delete': 'Delete GSM Subscription Request', 'gsm_document_upload': 'Upload GSM Subscription Document', 'gsm_document_delete': 'Delete GSM Subscription Document',
    'mobile_phone_create': 'Create Mobile Phone', 'mobile_phone_update': 'Update Mobile Phone', 'mobile_phone_delete': 'Delete Mobile Phone',
    'mobile_phone_quick_assign': 'Quick Assign Mobile Phone', 'mobile_phone_copy_paste_import': 'Bulk Import Mobile Phone',
    'create_network_switch': 'Create Switch', 'update_network_switch': 'Update Switch', 'delete_network_switch': 'Delete Switch', 'switch_backup': 'Switch Backup',
    'create_smart_scale': 'Create Smart Scale', 'update_smart_scale': 'Update Smart Scale', 'delete_smart_scale': 'Delete Smart Scale', 'calibrate_smart_scale': 'Calibrate Smart Scale',
    'cabinet_create': 'Create Cabinet', 'cabinet_update': 'Update Cabinet', 'cabinet_delete': 'Delete Cabinet',
    'create_circuit': 'Create Circuit', 'update_circuit': 'Update Circuit', 'delete_circuit': 'Delete Circuit',
    'create_technical_service': 'Create Technical Service', 'update_technical_service': 'Update Technical Service', 'delete_technical_service': 'Delete Technical Service',
    'create_meeting': 'Create Meeting', 'update_meeting': 'Update Meeting', 'delete_meeting': 'Delete Meeting',
    'create_iot_device': 'Create IoT Device', 'update_iot_device': 'Update IoT Device', 'delete_iot_device': 'Delete IoT Device',
    'create_tariff': 'Create Tariff', 'update_tariff': 'Update Tariff', 'delete_tariff': 'Delete Tariff',
    'upload_driver': 'Upload Driver', 'download_driver': 'Download Driver',
    'upload_invoice': 'Upload Invoice', 'delete_invoice': 'Delete Invoice',
    'upload_logo': 'Upload Logo', 'delete_logo': 'Delete Logo', 'upload_favicon': 'Upload Favicon',
    'create_announcement': 'Create Announcement', 'update_announcement': 'Update Announcement', 'delete_announcement': 'Delete Announcement',
    'view': 'View', 'export': 'Export', 'import': 'Import', 'sql_import_gsm_line': 'GSM Line SQL Import',
    'create_assignment_form': 'Assignment Form Created', 'create_return_form': 'Return Form Created',
    'device_assigned_to_me': 'Device Assigned to Me', 'device_returned_by_me': 'Device Returned',
    'equipment_assigned': 'Equipment Assigned', 'equipment_returned': 'Equipment Returned',
    'zimmet_approved_digital': 'Assignment Digitally Approved', 'zimmet_rejected_digital': 'Assignment Digitally Rejected',
    'computer_assigned': 'Computer Assigned', 'monitor_assigned': 'Monitor Assigned', 'mobile_phone_assigned': 'Mobile Phone Assigned'
  } : {
    'login': 'Giriş', 'logout': 'Çıkış', 'failed_login': 'Başarısız Giriş',
    'create_user': 'Kullanıcı Oluşturma', 'update_user': 'Kullanıcı Güncelleme', 'delete_user': 'Kullanıcı Silme',
    'create_order': 'Sipariş Oluşturma', 'update_order': 'Sipariş Güncelleme', 'delete_order': 'Sipariş Silme',
    'approve_order': 'Sipariş Onaylama', 'complete_order': 'Sipariş Tamamlama', 'cancel_order': 'Sipariş İptal',
    'create_capex': 'Capex Oluşturma', 'update_capex': 'Capex Güncelleme', 'delete_capex': 'Capex Silme',
    'use_capex': 'Capex Kullanma', 'block_capex': 'Capex Engelleme', 'clear_blocked_capex': 'Capex Engeli Kaldırma',
    'add_equipment': 'Ekipman Ekleme', 'update_equipment': 'Ekipman Güncelleme', 'delete_equipment': 'Ekipman Silme',
    'add_consumable': 'Sarf Malzeme Ekleme', 'update_consumable': 'Sarf Malzeme Güncelleme', 'delete_consumable': 'Sarf Malzeme Silme', 'assign_consumable': 'Sarf Malzeme Atama',
    'add_printer': 'Yazıcı Ekleme', 'update_printer': 'Yazıcı Güncelleme', 'delete_printer': 'Yazıcı Silme',
    'add_brand': 'Yazıcı Markası Ekleme', 'update_brand': 'Yazıcı Markası Güncelleme', 'delete_brand': 'Yazıcı Markası Silme',
    'add_model': 'Yazıcı Modeli Ekleme', 'update_model': 'Yazıcı Modeli Güncelleme', 'delete_model': 'Yazıcı Modeli Silme',
    'create_location': 'Lokasyon Oluşturma', 'update_location': 'Lokasyon Güncelleme', 'delete_location': 'Lokasyon Silme',
    'create_company': 'Şirket Oluşturma', 'update_company': 'Şirket Güncelleme', 'delete_company': 'Şirket Silme',
    'create_supplier': 'Tedarikçi Oluşturma', 'update_supplier': 'Tedarikçi Güncelleme', 'delete_supplier': 'Tedarikçi Silme',
    'create_gsm_line': 'GSM Hattı Oluşturma', 'update_gsm_line': 'GSM Hattı Güncelleme', 'delete_gsm_line': 'GSM Hattı Silme', 'quick_assign_gsm_line': 'GSM Hattı Hızlı Atama', 'gsm_subscription_create': 'GSM Abonelik Talebi Oluşturma', 'gsm_subscription_status': 'GSM Abonelik Süreç Adımı', 'gsm_subscription_activate': 'GSM Hattı Aktif Etme', 'gsm_subscription_cancel': 'GSM Abonelik Talebi İptali', 'gsm_subscription_delete': 'GSM Abonelik Talebi Silme', 'gsm_document_upload': 'GSM Abonelik Evrakı Yükleme', 'gsm_document_delete': 'GSM Abonelik Evrakı Silme',
    'mobile_phone_create': 'Cep Telefonu Oluşturma', 'mobile_phone_update': 'Cep Telefonu Güncelleme', 'mobile_phone_delete': 'Cep Telefonu Silme',
    'mobile_phone_quick_assign': 'Cep Telefonu Hızlı Atama', 'mobile_phone_copy_paste_import': 'Cep Telefonu Toplu İçe Aktarma',
    'create_network_switch': 'Switch Oluşturma', 'update_network_switch': 'Switch Güncelleme', 'delete_network_switch': 'Switch Silme', 'switch_backup': 'Switch Yedekleme',
    'create_smart_scale': 'Akıllı Terazi Oluşturma', 'update_smart_scale': 'Akıllı Terazi Güncelleme', 'delete_smart_scale': 'Akıllı Terazi Silme', 'calibrate_smart_scale': 'Akıllı Terazi Kalibrasyon',
    'cabinet_create': 'Kabin Oluşturma', 'cabinet_update': 'Kabin Güncelleme', 'cabinet_delete': 'Kabin Silme',
    'create_circuit': 'Devre Oluşturma', 'update_circuit': 'Devre Güncelleme', 'delete_circuit': 'Devre Silme',
    'create_technical_service': 'Teknik Servis Oluşturma', 'update_technical_service': 'Teknik Servis Güncelleme', 'delete_technical_service': 'Teknik Servis Silme',
    'create_meeting': 'Toplantı Oluşturma', 'update_meeting': 'Toplantı Güncelleme', 'delete_meeting': 'Toplantı Silme',
    'create_iot_device': 'IoT Cihaz Oluşturma', 'update_iot_device': 'IoT Cihaz Güncelleme', 'delete_iot_device': 'IoT Cihaz Silme',
    'create_tariff': 'Tarife Oluşturma', 'update_tariff': 'Tarife Güncelleme', 'delete_tariff': 'Tarife Silme',
    'upload_driver': 'Driver Yükleme', 'download_driver': 'Driver İndirme',
    'upload_invoice': 'Fatura Yükleme', 'delete_invoice': 'Fatura Silme',
    'upload_logo': 'Logo Yükleme', 'delete_logo': 'Logo Silme', 'upload_favicon': 'Favicon Yükleme',
    'create_announcement': 'Duyuru Oluşturma', 'update_announcement': 'Duyuru Güncelleme', 'delete_announcement': 'Duyuru Silme',
    'view': 'Görüntüleme', 'export': 'Dışa Aktarma', 'import': 'İçe Aktarma', 'sql_import_gsm_line': 'GSM Hattı SQL İçe Aktarma',
    'create_assignment_form': 'Zimmet Formu Oluşturma', 'create_return_form': 'İade Formu Oluşturma',
    'device_assigned_to_me': 'Cihaz Zimmetlendi', 'device_returned_by_me': 'Cihaz İadesi',
    'equipment_assigned': 'Ekipman Zimmeti', 'equipment_returned': 'Ekipman İadesi',
    'zimmet_approved_digital': 'Dijital Zimmet Onayı', 'zimmet_rejected_digital': 'Dijital Zimmet Reddi',
    'computer_assigned': 'Bilgisayar Zimmeti', 'monitor_assigned': 'Monitör Zimmeti', 'mobile_phone_assigned': 'Telefon Zimmeti'
  };

  // Action types and their translations
  const actionTypes = [
    // Authentication
    { value: 'login', label: actionTypeLabels['login'], color: 'bg-green-100 text-green-800', icon: '🔐' },
    { value: 'logout', label: actionTypeLabels['logout'], color: 'bg-red-100 text-red-800', icon: '🚪' },
    { value: 'failed_login', label: actionTypeLabels['failed_login'], color: 'bg-red-100 text-red-800', icon: '❌' },
    // User Management
    { value: 'create_user', label: actionTypeLabels['create_user'], color: 'bg-primary/20 text-primary-dark', icon: '👤' },
    { value: 'update_user', label: actionTypeLabels['update_user'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_user', label: actionTypeLabels['delete_user'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Orders Management
    { value: 'create_order', label: actionTypeLabels['create_order'], color: 'bg-primary/20 text-primary-dark', icon: '📋' },
    { value: 'update_order', label: actionTypeLabels['update_order'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_order', label: actionTypeLabels['delete_order'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'approve_order', label: actionTypeLabels['approve_order'], color: 'bg-green-100 text-green-800', icon: '✅' },
    { value: 'complete_order', label: actionTypeLabels['complete_order'], color: 'bg-green-100 text-green-800', icon: '✅' },
    { value: 'cancel_order', label: actionTypeLabels['cancel_order'], color: 'bg-red-100 text-red-800', icon: '❌' },
    // Capex Management
    { value: 'create_capex', label: actionTypeLabels['create_capex'], color: 'bg-primary/20 text-primary-dark', icon: '💰' },
    { value: 'update_capex', label: actionTypeLabels['update_capex'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_capex', label: actionTypeLabels['delete_capex'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'use_capex', label: actionTypeLabels['use_capex'], color: 'bg-purple-100 text-purple-800', icon: '💸' },
    { value: 'block_capex', label: actionTypeLabels['block_capex'], color: 'bg-red-100 text-red-800', icon: '🚫' },
    { value: 'clear_blocked_capex', label: actionTypeLabels['clear_blocked_capex'], color: 'bg-green-100 text-green-800', icon: '🔓' },
    // Equipment Management
    { value: 'add_equipment', label: actionTypeLabels['add_equipment'], color: 'bg-primary/20 text-primary-dark', icon: '🖥️' },
    { value: 'update_equipment', label: actionTypeLabels['update_equipment'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_equipment', label: actionTypeLabels['delete_equipment'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Consumable Management
    { value: 'add_consumable', label: actionTypeLabels['add_consumable'], color: 'bg-primary/20 text-primary-dark', icon: '📦' },
    { value: 'update_consumable', label: actionTypeLabels['update_consumable'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_consumable', label: actionTypeLabels['delete_consumable'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'assign_consumable', label: actionTypeLabels['assign_consumable'], color: 'bg-purple-100 text-purple-800', icon: '📤' },
    // Printer Management
    { value: 'add_printer', label: actionTypeLabels['add_printer'], color: 'bg-primary/20 text-primary-dark', icon: '🖨️' },
    { value: 'update_printer', label: actionTypeLabels['update_printer'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_printer', label: actionTypeLabels['delete_printer'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'add_brand', label: actionTypeLabels['add_brand'], color: 'bg-primary/20 text-primary-dark', icon: '🏷️' },
    { value: 'update_brand', label: actionTypeLabels['update_brand'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_brand', label: actionTypeLabels['delete_brand'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'add_model', label: actionTypeLabels['add_model'], color: 'bg-primary/20 text-primary-dark', icon: '📋' },
    { value: 'update_model', label: actionTypeLabels['update_model'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_model', label: actionTypeLabels['delete_model'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Location Management
    { value: 'create_location', label: actionTypeLabels['create_location'], color: 'bg-primary/20 text-primary-dark', icon: '📍' },
    { value: 'update_location', label: actionTypeLabels['update_location'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_location', label: actionTypeLabels['delete_location'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Company Management
    { value: 'create_company', label: actionTypeLabels['create_company'], color: 'bg-primary/20 text-primary-dark', icon: '🏢' },
    { value: 'update_company', label: actionTypeLabels['update_company'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_company', label: actionTypeLabels['delete_company'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Supplier Management
    { value: 'create_supplier', label: actionTypeLabels['create_supplier'], color: 'bg-primary/20 text-primary-dark', icon: '🏪' },
    { value: 'update_supplier', label: actionTypeLabels['update_supplier'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_supplier', label: actionTypeLabels['delete_supplier'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // GSM Line Management
    { value: 'create_gsm_line', label: actionTypeLabels['create_gsm_line'], color: 'bg-primary/20 text-primary-dark', icon: '📱' },
    { value: 'update_gsm_line', label: actionTypeLabels['update_gsm_line'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_gsm_line', label: actionTypeLabels['delete_gsm_line'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'quick_assign_gsm_line', label: actionTypeLabels['quick_assign_gsm_line'], color: 'bg-purple-100 text-purple-800', icon: '⚡' },
    { value: 'gsm_subscription_create', label: actionTypeLabels['gsm_subscription_create'], color: 'bg-primary/20 text-primary-dark', icon: '📋' },
    { value: 'gsm_subscription_status', label: actionTypeLabels['gsm_subscription_status'], color: 'bg-blue-100 text-blue-800', icon: '➡️' },
    { value: 'gsm_subscription_activate', label: actionTypeLabels['gsm_subscription_activate'], color: 'bg-green-100 text-green-800', icon: '✅' },
    { value: 'gsm_subscription_cancel', label: actionTypeLabels['gsm_subscription_cancel'], color: 'bg-gray-200 text-gray-800', icon: '🚫' },
    { value: 'gsm_subscription_delete', label: actionTypeLabels['gsm_subscription_delete'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'gsm_document_upload', label: actionTypeLabels['gsm_document_upload'], color: 'bg-primary/20 text-primary-dark', icon: '📎' },
    { value: 'gsm_document_delete', label: actionTypeLabels['gsm_document_delete'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Mobile Phone Management
    { value: 'mobile_phone_create', label: actionTypeLabels['mobile_phone_create'], color: 'bg-primary/20 text-primary-dark', icon: '📱' },
    { value: 'mobile_phone_update', label: actionTypeLabels['mobile_phone_update'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'mobile_phone_delete', label: actionTypeLabels['mobile_phone_delete'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'mobile_phone_quick_assign', label: actionTypeLabels['mobile_phone_quick_assign'], color: 'bg-purple-100 text-purple-800', icon: '⚡' },
    { value: 'mobile_phone_copy_paste_import', label: actionTypeLabels['mobile_phone_copy_paste_import'], color: 'bg-indigo-100 text-indigo-800', icon: '📥' },
    // Network Switch Management
    { value: 'create_network_switch', label: actionTypeLabels['create_network_switch'], color: 'bg-primary/20 text-primary-dark', icon: '🔌' },
    { value: 'update_network_switch', label: actionTypeLabels['update_network_switch'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_network_switch', label: actionTypeLabels['delete_network_switch'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'switch_backup', label: actionTypeLabels['switch_backup'], color: 'bg-green-100 text-green-800', icon: '💾' },
    // Smart Scale Management
    { value: 'create_smart_scale', label: actionTypeLabels['create_smart_scale'], color: 'bg-primary/20 text-primary-dark', icon: '⚖️' },
    { value: 'update_smart_scale', label: actionTypeLabels['update_smart_scale'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_smart_scale', label: actionTypeLabels['delete_smart_scale'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'calibrate_smart_scale', label: actionTypeLabels['calibrate_smart_scale'], color: 'bg-purple-100 text-purple-800', icon: '🔧' },
    // Cabinet Management
    { value: 'cabinet_create', label: actionTypeLabels['cabinet_create'], color: 'bg-primary/20 text-primary-dark', icon: '🏗️' },
    { value: 'cabinet_update', label: actionTypeLabels['cabinet_update'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'cabinet_delete', label: actionTypeLabels['cabinet_delete'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Circuit Management
    { value: 'create_circuit', label: actionTypeLabels['create_circuit'], color: 'bg-primary/20 text-primary-dark', icon: '🔗' },
    { value: 'update_circuit', label: actionTypeLabels['update_circuit'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_circuit', label: actionTypeLabels['delete_circuit'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Technical Service Management
    { value: 'create_technical_service', label: actionTypeLabels['create_technical_service'], color: 'bg-primary/20 text-primary-dark', icon: '🔧' },
    { value: 'update_technical_service', label: actionTypeLabels['update_technical_service'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_technical_service', label: actionTypeLabels['delete_technical_service'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Meeting Management
    { value: 'create_meeting', label: actionTypeLabels['create_meeting'], color: 'bg-primary/20 text-primary-dark', icon: '📅' },
    { value: 'update_meeting', label: actionTypeLabels['update_meeting'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_meeting', label: actionTypeLabels['delete_meeting'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // IoT Device Management
    { value: 'create_iot_device', label: actionTypeLabels['create_iot_device'], color: 'bg-primary/20 text-primary-dark', icon: '🌐' },
    { value: 'update_iot_device', label: actionTypeLabels['update_iot_device'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_iot_device', label: actionTypeLabels['delete_iot_device'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Tariff Management
    { value: 'create_tariff', label: actionTypeLabels['create_tariff'], color: 'bg-primary/20 text-primary-dark', icon: '💰' },
    { value: 'update_tariff', label: actionTypeLabels['update_tariff'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_tariff', label: actionTypeLabels['delete_tariff'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // File Operations
    { value: 'upload_driver', label: actionTypeLabels['upload_driver'], color: 'bg-primary/20 text-primary-dark', icon: '📁' },
    { value: 'download_driver', label: actionTypeLabels['download_driver'], color: 'bg-green-100 text-green-800', icon: '⬇️' },
    { value: 'upload_invoice', label: actionTypeLabels['upload_invoice'], color: 'bg-primary/20 text-primary-dark', icon: '📄' },
    { value: 'delete_invoice', label: actionTypeLabels['delete_invoice'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'upload_logo', label: actionTypeLabels['upload_logo'], color: 'bg-primary/20 text-primary-dark', icon: '🖼️' },
    { value: 'delete_logo', label: actionTypeLabels['delete_logo'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    { value: 'upload_favicon', label: actionTypeLabels['upload_favicon'], color: 'bg-primary/20 text-primary-dark', icon: '🌟' },
    // Announcement Management
    { value: 'create_announcement', label: actionTypeLabels['create_announcement'], color: 'bg-primary/20 text-primary-dark', icon: '📢' },
    { value: 'update_announcement', label: actionTypeLabels['update_announcement'], color: 'bg-yellow-100 text-yellow-800', icon: '✏️' },
    { value: 'delete_announcement', label: actionTypeLabels['delete_announcement'], color: 'bg-red-100 text-red-800', icon: '🗑️' },
    // Generic Actions
    { value: 'view', label: actionTypeLabels['view'], color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200', icon: '👁️' },
    { value: 'export', label: actionTypeLabels['export'], color: 'bg-purple-100 text-purple-800', icon: '📤' },
    { value: 'import', label: actionTypeLabels['import'], color: 'bg-indigo-100 text-indigo-800', icon: '📥' },
    { value: 'sql_import_gsm_line', label: actionTypeLabels['sql_import_gsm_line'], color: 'bg-indigo-100 text-indigo-800', icon: '📥' },
    // Zimmet operations
    { value: 'create_assignment_form', label: actionTypeLabels['create_assignment_form'], color: 'bg-indigo-100 text-indigo-800', icon: '📄' },
    { value: 'create_return_form', label: actionTypeLabels['create_return_form'], color: 'bg-orange-100 text-orange-800', icon: '↩️' },
    { value: 'device_assigned_to_me', label: actionTypeLabels['device_assigned_to_me'], color: 'bg-blue-100 text-blue-800', icon: '📦' },
    { value: 'device_returned_by_me', label: actionTypeLabels['device_returned_by_me'], color: 'bg-orange-100 text-orange-800', icon: '↩️' },
    { value: 'equipment_assigned', label: actionTypeLabels['equipment_assigned'], color: 'bg-indigo-100 text-indigo-800', icon: '🔧' },
    { value: 'equipment_returned', label: actionTypeLabels['equipment_returned'], color: 'bg-orange-100 text-orange-800', icon: '↩️' },
    { value: 'zimmet_approved_digital', label: actionTypeLabels['zimmet_approved_digital'], color: 'bg-green-100 text-green-800', icon: '✅' },
    { value: 'zimmet_rejected_digital', label: actionTypeLabels['zimmet_rejected_digital'], color: 'bg-red-100 text-red-800', icon: '❌' }
  ];

  // Action translations (past tense, language-aware)
  const actionTranslations: { [key: string]: string } = language === 'en' ? {
    'login': 'Logged In',
    'logout': 'Logged Out',
    'failed_login': 'Failed Login',
    'password_change': 'Password Changed',
    'password_reset': 'Password Reset',
    'password_reset_request': 'Password Reset Requested',
    'send_login_reminder': 'Login Reminder Sent',
    'create_user': 'User Created',
    'update_user': 'User Updated',
    'delete_user': 'User Deleted',
    'create_order': 'Order Created', 'update_order': 'Order Updated', 'delete_order': 'Order Deleted',
    'approve_order': 'Order Approved', 'complete_order': 'Order Completed', 'cancel_order': 'Order Cancelled',
    'approve_invoice': 'Invoice Approved',
    'create_capex': 'CAPEX Created', 'update_capex': 'CAPEX Updated', 'delete_capex': 'CAPEX Deleted',
    'use_capex': 'CAPEX Used', 'block_capex': 'CAPEX Blocked', 'clear_blocked_capex': 'CAPEX Unblocked',
    'debt_created': 'Debt Created',
    'add_equipment': 'Equipment Added', 'update_equipment': 'Equipment Updated', 'delete_equipment': 'Equipment Deleted',
    'add_consumable': 'Consumable Added', 'update_consumable': 'Consumable Updated', 'delete_consumable': 'Consumable Deleted', 'assign_consumable': 'Consumable Assigned',
    'add_printer': 'Printer Added', 'update_printer': 'Printer Updated', 'delete_printer': 'Printer Deleted',
    'add_brand': 'Brand Added', 'update_brand': 'Brand Updated', 'delete_brand': 'Brand Deleted',
    'add_model': 'Model Added', 'update_model': 'Model Updated', 'delete_model': 'Model Deleted',
    'upload_driver': 'Driver Uploaded', 'download_driver': 'Driver Downloaded',
    'delete_switch_model': 'Switch Model Deleted', 'update_switch_model': 'Switch Model Updated',
    'create_location': 'Location Created', 'update_location': 'Location Updated', 'delete_location': 'Location Deleted',
    'create_company': 'Company Created', 'update_company': 'Company Updated', 'delete_company': 'Company Deleted',
    'create_supplier': 'Supplier Created', 'update_supplier': 'Supplier Updated', 'delete_supplier': 'Supplier Deleted',
    'create_gsm_line': 'GSM Line Created', 'update_gsm_line': 'GSM Line Updated', 'delete_gsm_line': 'GSM Line Deleted', 'gsm_subscription_create': 'GSM Subscription Request Created', 'gsm_subscription_status': 'GSM Subscription Step Updated', 'gsm_subscription_activate': 'GSM Line Activated', 'gsm_subscription_cancel': 'GSM Subscription Request Cancelled', 'gsm_subscription_delete': 'GSM Subscription Request Deleted', 'gsm_document_upload': 'GSM Subscription Document Uploaded', 'gsm_document_delete': 'GSM Subscription Document Deleted',
    'quick_assign_gsm_line': 'GSM Line Quick Assigned', 'sql_import_gsm_line': 'GSM Lines SQL Imported',
    'mobile_phone_create': 'Mobile Phone Created', 'mobile_phone_update': 'Mobile Phone Updated', 'mobile_phone_delete': 'Mobile Phone Deleted',
    'mobile_phone_quick_assign': 'Mobile Phone Quick Assigned', 'mobile_phone_copy_paste_import': 'Mobile Phones Bulk Imported',
    'create_network_switch': 'Switch Created', 'update_network_switch': 'Switch Updated', 'delete_network_switch': 'Switch Deleted',
    'switch_backup': 'Switch Backed Up',
    'create_vlan': 'VLAN Created', 'update_vlan': 'VLAN Updated', 'delete_vlan': 'VLAN Deleted',
    'create_circuit': 'Circuit Created', 'update_circuit': 'Circuit Updated', 'delete_circuit': 'Circuit Deleted',
    'cabinet_create': 'Cabinet Created', 'cabinet_update': 'Cabinet Updated', 'cabinet_delete': 'Cabinet Deleted',
    'create_smart_scale': 'Smart Scale Created', 'update_smart_scale': 'Smart Scale Updated', 'delete_smart_scale': 'Smart Scale Deleted',
    'calibrate_smart_scale': 'Smart Scale Calibrated',
    'create_radio': 'Radio Created', 'update_radio': 'Radio Updated', 'delete_radio': 'Radio Deleted',
    'assign_radio_user': 'Radio User Assigned', 'unassign_radio_user': 'Radio User Unassigned',
    'create_handheld_terminal': 'Handheld Terminal Created', 'update_handheld_terminal': 'Handheld Terminal Updated', 'delete_handheld_terminal': 'Handheld Terminal Deleted',
    'create_physical_server': 'Physical Server Created', 'update_physical_server': 'Physical Server Updated', 'delete_physical_server': 'Physical Server Deleted',
    'create_virtual_server': 'Virtual Server Created', 'update_virtual_server': 'Virtual Server Updated', 'delete_virtual_server': 'Virtual Server Deleted',
    'create_meeting': 'Meeting Created', 'update_meeting': 'Meeting Updated', 'delete_meeting': 'Meeting Deleted',
    'create_nvr': 'NVR Created', 'update_nvr': 'NVR Updated', 'delete_nvr': 'NVR Deleted',
    'create_camera': 'Camera Created', 'update_camera': 'Camera Updated', 'delete_camera': 'Camera Deleted',
    'add_camera': 'Camera Added', 'remove_camera': 'Camera Removed',
    'create_storage_unit': 'Storage Unit Created', 'update_storage_unit': 'Storage Unit Updated', 'delete_storage_unit': 'Storage Unit Deleted',
    'create_iot_device': 'IoT Device Created', 'update_iot_device': 'IoT Device Updated', 'delete_iot_device': 'IoT Device Deleted',
    'create_tariff': 'Tariff Created', 'update_tariff': 'Tariff Updated', 'delete_tariff': 'Tariff Deleted',
    'upload_invoice': 'Invoice Uploaded', 'delete_invoice': 'Invoice Deleted',
    'upload_logo': 'Logo Uploaded', 'delete_logo': 'Logo Deleted', 'upload_favicon': 'Favicon Uploaded',
    'create_announcement': 'Announcement Created', 'update_announcement': 'Announcement Updated', 'delete_announcement': 'Announcement Deleted',
    'create': 'Created', 'update': 'Updated', 'delete': 'Deleted', 'view': 'Viewed', 'export': 'Exported', 'import': 'Imported',
    'create_assignment_form': 'Assignment Form Created', 'create_return_form': 'Return Form Created',
    'device_assigned_to_me': 'Device Assigned', 'device_returned_by_me': 'Device Returned',
    'equipment_assigned': 'Equipment Assigned', 'equipment_returned': 'Equipment Returned',
    'zimmet_approved_digital': 'Digitally Approved', 'zimmet_rejected_digital': 'Digitally Rejected'
  } : {
    // Auth actions
    'login': 'Giriş Yapıldı',
    'logout': 'Çıkış Yapıldı',
    'failed_login': 'Başarısız Giriş',
    'password_change': 'Şifre Değiştirildi',
    'password_reset': 'Şifre Sıfırlandı',
    'password_reset_request': 'Şifre Sıfırlama İsteği',
    'send_login_reminder': 'Giriş Hatırlatması Gönderildi',

    // User actions
    'create_user': 'Kullanıcı Oluşturuldu',
    'update_user': 'Kullanıcı Güncellendi',
    'delete_user': 'Kullanıcı Silindi',
    
    // Order actions
    'create_order': 'Sipariş Oluşturuldu',
    'update_order': 'Sipariş Güncellendi',
    'delete_order': 'Sipariş Silindi',
    'approve_order': 'Sipariş Onaylandı',
    'complete_order': 'Sipariş Tamamlandı',
    'cancel_order': 'Sipariş İptal Edildi',
    'approve_invoice': 'Fatura Onaylandı',
    
    // Capex actions
    'create_capex': 'Capex Oluşturuldu',
    'update_capex': 'Capex Güncellendi',
    'delete_capex': 'Capex Silindi',
    'use_capex': 'Capex Kullanıldı',
    'block_capex': 'Capex Engellendi',
    'clear_blocked_capex': 'Capex Engeli Kaldırıldı',
    'debt_created': 'Borç Oluşturuldu',
    
    // Equipment actions
    'add_equipment': 'Ekipman Eklendi',
    'update_equipment': 'Ekipman Güncellendi',
    'delete_equipment': 'Ekipman Silindi',
    
    // Consumable actions
    'add_consumable': 'Sarf Malzeme Eklendi',
    'update_consumable': 'Sarf Malzeme Güncellendi',
    'delete_consumable': 'Sarf Malzeme Silindi',
    'assign_consumable': 'Sarf Malzeme Atandı',
    
    // Printer actions
    'add_printer': 'Yazıcı Eklendi',
    'update_printer': 'Yazıcı Güncellendi',
    'delete_printer': 'Yazıcı Silindi',
    'add_brand': 'Yazıcı Markası Eklendi',
    'update_brand': 'Yazıcı Markası Güncellendi',
    'delete_brand': 'Yazıcı Markası Silindi',
    'add_model': 'Yazıcı Modeli Eklendi',
    'update_model': 'Yazıcı Modeli Güncellendi',
    'delete_model': 'Yazıcı Modeli Silindi',
    'delete_switch_model': 'Switch Modeli Silindi',
    'update_switch_model': 'Switch Modeli Güncellendi',
    'upload_driver': 'Driver Yüklendi',
    'download_driver': 'Driver İndirildi',
    
    // Location actions
    'create_location': 'Lokasyon Oluşturuldu',
    'update_location': 'Lokasyon Güncellendi',
    'delete_location': 'Lokasyon Silindi',
    
    // Company actions
    'create_company': 'Şirket Oluşturuldu',
    'update_company': 'Şirket Güncellendi',
    'delete_company': 'Şirket Silindi',
    
    // Supplier actions
    'create_supplier': 'Tedarikçi Oluşturuldu',
    'update_supplier': 'Tedarikçi Güncellendi',
    'delete_supplier': 'Tedarikçi Silindi',
    
    // GSM Line actions
    'create_gsm_line': 'GSM Hattı Oluşturuldu',
    'update_gsm_line': 'GSM Hattı Güncellendi',
    'delete_gsm_line': 'GSM Hattı Silindi',
    'gsm_subscription_create': 'GSM Abonelik Talebi Oluşturuldu',
    'gsm_subscription_status': 'GSM Abonelik Adımı Güncellendi',
    'gsm_subscription_activate': 'GSM Hattı Aktif Edildi',
    'gsm_subscription_cancel': 'GSM Abonelik Talebi İptal Edildi',
    'gsm_subscription_delete': 'GSM Abonelik Talebi Silindi',
    'gsm_document_upload': 'GSM Abonelik Evrakı Yüklendi',
    'gsm_document_delete': 'GSM Abonelik Evrakı Silindi',
    'quick_assign_gsm_line': 'GSM Hattı Hızlı Atandı',
    'sql_import_gsm_line': 'GSM Hattı SQL İçe Aktarıldı',
    
    // Mobile Phone actions
    'mobile_phone_create': 'Cep Telefonu Oluşturuldu',
    'mobile_phone_update': 'Cep Telefonu Güncellendi',
    'mobile_phone_delete': 'Cep Telefonu Silindi',
    'mobile_phone_quick_assign': 'Cep Telefonu Hızlı Atandı',
    'mobile_phone_copy_paste_import': 'Cep Telefonu Toplu İçe Aktarıldı',
    
    // Network Switch actions
    'create_network_switch': 'Switch Oluşturuldu',
    'update_network_switch': 'Switch Güncellendi',
    'delete_network_switch': 'Switch Silindi',
    'switch_backup': 'Switch Yedeklendi',
    'update_vlan': 'VLAN Güncellendi',
    
    // Smart Scale actions
    'create_smart_scale': 'Akıllı Terazi Oluşturuldu',
    'update_smart_scale': 'Akıllı Terazi Güncellendi',
    'delete_smart_scale': 'Akıllı Terazi Silindi',
    'calibrate_smart_scale': 'Akıllı Terazi Kalibre Edildi',
    
    // Cabinet actions
    'cabinet_create': 'Kabin Oluşturuldu',
    'cabinet_update': 'Kabin Güncellendi',
    'cabinet_delete': 'Kabin Silindi',
    
    // Circuit actions
    'create_circuit': 'Devre Oluşturuldu',
    'update_circuit': 'Devre Güncellendi',
    'delete_circuit': 'Devre Silindi',
    
    // Technical Service actions
    'create_technical_service': 'Teknik Servis Oluşturuldu',
    'update_technical_service': 'Teknik Servis Güncellendi',
    'delete_technical_service': 'Teknik Servis Silindi',
    
    // Meeting actions
    'create_meeting': 'Toplantı Oluşturuldu',
    'update_meeting': 'Toplantı Güncellendi',
    'delete_meeting': 'Toplantı Silindi',
    
    // IoT Device actions
    'create_iot_device': 'IoT Cihaz Oluşturuldu',
    'update_iot_device': 'IoT Cihaz Güncellendi',
    'delete_iot_device': 'IoT Cihaz Silindi',
    
    // Tariff actions
    'create_tariff': 'Tarife Oluşturuldu',
    'update_tariff': 'Tarife Güncellendi',
    'delete_tariff': 'Tarife Silindi',
    
    // File operations
    'upload_invoice': 'Fatura Yüklendi',
    'delete_invoice': 'Fatura Silindi',
    'upload_logo': 'Logo Yüklendi',
    'delete_logo': 'Logo Silindi',
    'upload_favicon': 'Favicon Yüklendi',
    
    // Announcement actions
    'create_announcement': 'Duyuru Oluşturuldu',
    'update_announcement': 'Duyuru Güncellendi',
    'delete_announcement': 'Duyuru Silindi',
    
    // Generic actions
    'create': 'Oluşturuldu',
    'update': 'Güncellendi',
    'delete': 'Silindi',
    'view': 'Görüntülendi',
    'export': 'Dışa Aktarıldı',
    'import': 'İçe Aktarıldı',

    // Zimmet actions
    'create_assignment_form': 'Zimmet Formu Oluşturuldu',
    'create_return_form': 'İade Formu Oluşturuldu',
    'device_assigned_to_me': 'Cihaz Zimmetlendi',
    'device_returned_by_me': 'Cihaz İade Edildi',
    'equipment_assigned': 'Ekipman Zimmetlendi',
    'equipment_returned': 'Ekipman İade Edildi',
    'zimmet_approved_digital': 'Dijital Olarak Onaylandı',
    'zimmet_rejected_digital': 'Dijital Olarak Reddedildi'
  };

  // Get action label with fallback
  const getActionLabel = (action: string) => {
    if (!action) return t('activity.unknownAction');
    
    if (actionTranslations[action]) {
      return actionTranslations[action];
    }
    
    if (action && typeof action === 'string' && action.length > 0) {
      try {
        return action.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      } catch (error) {
        console.warn('Error processing action:', action, error);
        return action;
      }
    }
    
    return t('activity.unknownAction');
  };

  // Get action color
  const getActionColor = (action: string) => {
    const actionType = actionTypes.find(a => a.value === action);
    if (actionType) return actionType.color;
    
    // Default colors based on action type
    if (action.includes('login')) return 'bg-green-100 text-green-800';
    if (action.includes('logout')) return 'bg-red-100 text-red-800';
    if (action.includes('create') || action.includes('add')) return 'bg-primary/20 text-primary-dark';
    if (action.includes('update') || action.includes('edit')) return 'bg-yellow-100 text-yellow-800';
    if (action.includes('delete') || action.includes('remove')) return 'bg-red-100 text-red-800';
    if (action.includes('view') || action.includes('read')) return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    if (action.includes('export')) return 'bg-purple-100 text-purple-800';
    if (action.includes('import')) return 'bg-indigo-100 text-indigo-800';
    
    return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  // Get action icon
  const getActionIcon = (action: string) => {
    const actionType = actionTypes.find(a => a.value === action);
    if (actionType) return actionType.icon;
    
    if (action.includes('login')) return '🔐';
    if (action.includes('logout')) return '🚪';
    if (action.includes('create') || action.includes('add')) return '➕';
    if (action.includes('update') || action.includes('edit')) return '✏️';
    if (action.includes('delete') || action.includes('remove')) return '🗑️';
    if (action.includes('view') || action.includes('read')) return '👁️';
    if (action.includes('export')) return '📤';
    if (action.includes('import')) return '📥';
    
    return '📋';
  };

  // Get device icon
  const getDeviceIcon = (userAgent: string) => {
    if (userAgent.includes('Mobile') || userAgent.includes('Android') || userAgent.includes('iPhone')) {
      return <Smartphone className="w-4 h-4 text-primary" />;
    }
    if (userAgent.includes('Windows')) {
      return <Monitor className="w-4 h-4 text-green-500" />;
    }
    if (userAgent.includes('Mac')) {
      return <Monitor className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
    }
    return <Globe className="w-4 h-4 text-gray-400" />;
  };

  // Get category from action or use existing category
  const getCategoryFromAction = (action: string, existingCategory?: string): string => {
    // If category already exists and is valid, use it
    if (existingCategory && categoryGroups.find(c => c.value === existingCategory && c.value !== 'all')) {
      return existingCategory;
    }
    
    // Try to map from action
    if (action && actionToCategoryMap[action]) {
      return actionToCategoryMap[action];
    }
    
    // Try to infer from action name
    const actionLower = String(action || '').toLowerCase();
    if (actionLower.includes('login') || actionLower.includes('logout') || actionLower.includes('password')) {
      return 'auth';
    }
    if (actionLower.includes('capex')) {
      return 'capex';
    }
    if (actionLower.includes('printer') || actionLower.includes('brand') || actionLower.includes('model') || actionLower.includes('driver')) {
      return 'printer';
    }
    if (actionLower.includes('consumable')) {
      return 'consumable';
    }
    if (actionLower.includes('switch') || actionLower.includes('circuit') || actionLower.includes('cabinet') || actionLower.includes('vlan') || actionLower.includes('network')) {
      return 'network';
    }
    if (actionLower.includes('nvr') || actionLower.includes('camera') || actionLower.includes('kamera')) {
      return 'nvr_camera';
    }
    if (actionLower.includes('storage') || actionLower.includes('depolama')) {
      return 'storage';
    }
    if (actionLower.includes('iot') || actionLower.includes('smart_scale') || actionLower.includes('scale') || actionLower.includes('radio') || actionLower.includes('handheld')) {
      return 'industrial';
    }
    if (actionLower.includes('server') || actionLower.includes('physical_server') || actionLower.includes('virtual_server')) {
      return 'server';
    }
    if (actionLower.includes('vlan')) {
      return 'network';
    }
    if (actionLower.includes('order') || actionLower.includes('invoice') || actionLower.includes('siparis')) {
      return 'order';
    }
    if (actionLower.includes('technical') || actionLower.includes('service')) {
      return 'technical';
    }
    if (actionLower.includes('gsm') || actionLower.includes('mobile_phone') || actionLower.includes('tariff')) {
      return 'gsm_telefon';
    }
    if (actionLower.includes('equipment') || actionLower.includes('company') || actionLower.includes('location') || actionLower.includes('supplier')) {
      return 'arac_gerec';
    }
    if (actionLower.includes('meeting')) {
      return 'meeting';
    }
    if (actionLower.includes('user') || actionLower.includes('announcement') || actionLower.includes('logo') || actionLower.includes('favicon')) {
      return 'settings';
    }
    
    return 'other';
  };

  // Group activities by category
  const groupActivitiesByCategory = (activities: UserActivity[]) => {
    const grouped: { [key: string]: UserActivity[] } = {};
    
    activities.forEach(activity => {
      // Get category from action or use existing category
      const category = getCategoryFromAction(activity.action, activity.category);
      
      // Normalize category value
      const normalizedCategory = category || 'other';
      
      if (!grouped[normalizedCategory]) {
        grouped[normalizedCategory] = [];
      }
      grouped[normalizedCategory].push({
        ...activity,
        category: normalizedCategory
      });
    });
    
    return grouped;
  };

  // Get category label
  const getCategoryLabel = (category: string) => {
    const categoryGroup = categoryGroups.find(c => c.value === category);
    if (categoryGroup) return categoryGroup.label;
    
    // Fallback: capitalize first letter
    if (!category) return t('activity.cat.other');
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    const categoryGroup = categoryGroups.find(c => c.value === category);
    if (categoryGroup) return categoryGroup.icon;
    return '📋';
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(language === 'en' ? 'en-GB' : 'tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Load activities data
  const loadActivities = async (page = 1) => {
    try {
      setLoading(true);
      
      // Sayfa numarasını integer'a çevir
      const pageNum = typeof page === 'number' ? page : parseInt(String(page)) || 1;
      
      const params: any = {
        page: pageNum,
        limit: itemsPerPage,
        start_date: dateRange.start,
        end_date: dateRange.end
      };

      if (searchTerm) params.search = searchTerm;
      if (selectedUser > 0) params.user_id = selectedUser;
      if (selectedAction !== 'all') params.action = selectedAction;
      if (selectedCategory !== 'all') params.category = selectedCategory;

      const query = new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)])
      ).toString();
      const response = await apiService.get(`/user-activities?${query}`);
      
      if (response && response.activities) {
        setActivities(response.activities);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.total || 0);
        setCurrentPage(pageNum);
        
        // Sayfa değiştiğinde expanded categories'i temizle
        // Böylece her sayfa için kategoriler baştan açılabilir
        if (groupByCategory) {
          setExpandedCategories(new Set());
        }
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      showGlobalNotification('Aktiviteler yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load users data
  const loadUsers = async () => {
    try {
      const response = await apiService.get('/users');
      if (response && Array.isArray(response)) {
        setUsers(response);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadUsers();
    loadActivities(1);
  }, []);

  // Reload when filters change
  useEffect(() => {
    loadActivities(1);
  }, [searchTerm, selectedUser, selectedAction, selectedCategory, dateRange]);

  // Export functions
  const exportToCSV = () => {
    const headers = [t('activity.col.date'), t('activity.col.user'), t('activity.col.action'), t('activity.col.description'), t('activity.col.ip'), t('activity.col.device')];
    const csvContent = [
      headers.join(','),
      ...activities.map(activity => [
        formatDate(activity.created_at),
        activity.user_name,
        getActionLabel(activity.action),
        activity.description,
        activity.ip_address,
        activity.user_agent.includes('Mobile') ? t('activity.mobile') : t('activity.desktop')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `user-activities-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    // Simple HTML table export for Excel
    const table = document.createElement('table');
    const headers = [t('activity.col.date'), t('activity.col.user'), t('activity.col.action'), t('activity.col.description'), t('activity.col.ip'), t('activity.col.device')];
    
    const headerRow = document.createElement('tr');
    headers.forEach(header => {
      const th = document.createElement('th');
      th.textContent = header;
      headerRow.appendChild(th);
    });
    table.appendChild(headerRow);

    activities.forEach(activity => {
      const row = document.createElement('tr');
      const cells = [
        formatDate(activity.created_at),
        activity.user_name,
        getActionLabel(activity.action),
        activity.description,
        activity.ip_address,
        activity.user_agent.includes('Mobile') ? t('activity.mobile') : t('activity.desktop')
      ];

      cells.forEach(cellData => {
        const td = document.createElement('td');
        td.textContent = cellData;
        row.appendChild(td);
      });
      table.appendChild(row);
    });

    const htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>${t('activity.exportTitle')}</title>
        </head>
        <body>
          <h1>${t('activity.exportTitle')} - ${new Date().toLocaleDateString(language === 'en' ? 'en-GB' : 'tr-TR')}</h1>
          ${table.outerHTML}
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `user-activities-${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 md:p-6">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                <Activity className="w-5 h-5 md:w-6 md:h-6 mr-2 text-primary" />
                {t('activity.title')}
              </h1>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">
                {t('activity.subtitle')}
              </p>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('activity.export')}
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg shadow-lg z-10">
                  <button
                    onClick={() => { exportToCSV(); setShowExportMenu(false); }}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {t('activity.exportCSV')}
                  </button>
                  <button
                    onClick={() => { exportToExcel(); setShowExportMenu(false); }}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    {t('activity.exportExcel')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 md:mb-6 flex flex-col md:flex-row md:flex-wrap gap-3 md:items-center">
          <div className="relative w-full md:flex-1 md:min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('activity.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <CollapsibleFilters
            activeCount={[selectedUser > 0, selectedAction !== 'all', selectedCategory !== 'all', dateRange.start !== todayStr || dateRange.end !== todayStr].filter(Boolean).length}
            onClear={() => {
              setSelectedUser(0);
              setSelectedAction('all');
              setSelectedCategory('all');
              setDateRange({ start: todayStr, end: todayStr });
            }}
          >
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value={0}>{t('activity.allUsers')}</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.name} ({user.role})</option>
            ))}
          </select>
          <select
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">{t('activity.allActions')}</option>
            {actionTypes.map(action => (
              <option key={action.value} value={action.value}>{action.icon} {action.label}</option>
            ))}
          </select>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {categoryGroups.map(category => (
              <option key={category.value} value={category.value}>{category.icon} {category.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          </CollapsibleFilters>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 md:mb-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-3 bg-primary/20 dark:bg-primary/90 rounded-lg">
                <Activity className="w-6 h-6 text-primary dark:text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('activity.totalActivities')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">{totalItems}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                <User className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('activity.activeUsers')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">{users.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('activity.today')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">
                  {activities.filter(a => new Date(a.created_at).toDateString() === new Date().toDateString()).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Globe className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('activity.page')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">{currentPage} / {totalPages}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Activities List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                {t('activity.list')}
              </h2>
              <div className="flex items-center space-x-3">
                {groupByCategory && activities.length > 0 && (
                  <button
                    onClick={() => {
                      const grouped = groupActivitiesByCategory(activities);
                      const categories = Object.keys(grouped);
                      const allExpanded = categories.length > 0 && categories.every(cat => expandedCategories.has(cat));
                      if (allExpanded) {
                        setExpandedCategories(new Set());
                      } else {
                        setExpandedCategories(new Set(categories));
                      }
                    }}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white transition-colors"
                  >
                    {(() => {
                      const grouped = groupActivitiesByCategory(activities);
                      const categories = Object.keys(grouped);
                      const allExpanded = categories.length > 0 && categories.every(cat => expandedCategories.has(cat));
                      return allExpanded ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          {t('activity.collapseAll')}
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          {t('activity.expandAll')}
                        </>
                      );
                    })()}
                  </button>
                )}
                <button
                  onClick={() => setGroupByCategory(!groupByCategory)}
                  className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    groupByCategory
                      ? 'bg-primary/20 dark:bg-primary/90 text-primary-dark dark:text-accent'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  {groupByCategory ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                  {groupByCategory ? t('activity.categorizedView') : t('activity.normalView')}
                </button>
                <button
                  onClick={() => loadActivities(currentPage)}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('activity.refresh')}
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center">
                <RefreshCw className="w-6 h-6 mr-2 animate-spin text-primary" />
                <span className="text-gray-600 dark:text-gray-400">{t('activity.loading')}</span>
              </div>
            </div>
          ) : activities.length === 0 ? (
            <div className="p-8 text-center">
              <Activity className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">{t('activity.notFound')}</p>
            </div>
          ) : groupByCategory ? (
            // Categorized view
            (() => {
              const grouped = groupActivitiesByCategory(activities);
              
              // Sort categories by predefined order (sidebar order)
              const categoryOrder = ['auth', 'capex', 'printer', 'consumable', 'network', 'nvr_camera', 'industrial', 'order', 'technical', 'gsm_telefon', 'arac_gerec', 'server', 'storage', 'meeting', 'zimmet', 'settings', 'other'];
              const sortedCategories = Object.keys(grouped).sort((a, b) => {
                const aIndex = categoryOrder.indexOf(a);
                const bIndex = categoryOrder.indexOf(b);
                
                // If both are in the order list, sort by their position
                if (aIndex !== -1 && bIndex !== -1) {
                  return aIndex - bIndex;
                }
                
                // If only one is in the list, prioritize it
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;
                
                // If neither is in the list, sort alphabetically
                const aLabel = getCategoryLabel(a);
                const bLabel = getCategoryLabel(b);
                return aLabel.localeCompare(bLabel, 'tr');
              });
              
              return (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedCategories.map((category) => {
                    const isExpanded = expandedCategories.has(category);
                    return (
                      <div key={category} className="py-4">
                        {/* Category Header - Clickable */}
                        <button
                          onClick={() => toggleCategory(category)}
                          className="w-full px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 dark:border-gray-600 sticky top-0 z-10 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-lg">{getCategoryIcon(category)}</span>
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                                {getCategoryLabel(category)}
                              </h3>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/20 dark:bg-primary/90 text-primary-dark dark:text-accent">
                                {grouped[category].length}
                              </span>
                            </div>
                            <ChevronDown 
                              className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                            />
                          </div>
                        </button>
                        
                        {/* Category Activities - Collapsible */}
                        {isExpanded && (
                          <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {grouped[category].map((activity) => (
                          <div key={activity.id} className="p-6 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 rounded-full flex items-center justify-center">
                                    <span className="text-lg">{getActionIcon(activity.action)}</span>
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white">
                                      {activity.user_name}
                                    </span>
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(activity.action)}`}>
                                      {getActionLabel(activity.action)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {activity.description}
                                  </p>
                                  <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                                    <div className="flex items-center">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {formatDate(activity.created_at)}
                                    </div>
                                    <div className="flex items-center">
                                      <Globe className="w-3 h-3 mr-1" />
                                      {activity.ip_address}
                                    </div>
                                    <div className="flex items-center">
                                      {getDeviceIcon(activity.user_agent)}
                                      <span className="ml-1">
                                        {activity.user_agent.includes('Mobile') ? t('activity.mobile') : t('activity.desktop')}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            // Normal view (non-categorized)
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {activities.map((activity) => (
                <div key={activity.id} className="p-6 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 rounded-full flex items-center justify-center">
                          <span className="text-lg">{getActionIcon(activity.action)}</span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white">
                            {activity.user_name}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(activity.action)}`}>
                            {getActionLabel(activity.action)}
                          </span>
                          {activity.category && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                              {getCategoryIcon(activity.category)} {getCategoryLabel(activity.category)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {activity.description}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDate(activity.created_at)}
                          </div>
                          <div className="flex items-center">
                            <Globe className="w-3 h-3 mr-1" />
                            {activity.ip_address}
                          </div>
                          <div className="flex items-center">
                            {getDeviceIcon(activity.user_agent)}
                            <span className="ml-1">
                              {activity.user_agent.includes('Mobile') ? 'Mobil' : 'Desktop'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {language === 'en'
                    ? `Total ${totalItems} activities, page ${currentPage} / ${totalPages}`
                    : `Toplam ${totalItems} aktivite, sayfa ${currentPage} / ${totalPages}`}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => loadActivities(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('activity.previous')}
                  </button>
                  <button
                    onClick={() => loadActivities(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('activity.next')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default UserActivityPage;